import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Loader2, Plus, Pencil, Trash2, X, Check, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';

/**
 * Departments master management (Admin). Tenant-wide single source of truth
 * used across the Safety module. Backend:
 *   GET    /api/departments?include_inactive=true  -> { departments, can_manage }
 *   POST   /api/departments      { name, code, description }
 *   PUT    /api/departments/{id} { name, code, description, is_active }
 *   DELETE /api/departments/{id}
 */
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const EMPTY = { name: '', code: '', description: '' };

const DepartmentManagement = () => {
  const { confirm, showError, showSuccess } = useAlert();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [canManage, setCanManage] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/departments?include_inactive=true`, authHeaders());
      setDepartments(res.data.departments || []);
      setCanManage(!!res.data.can_manage);
    } catch (e) {
      showError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormError(''); setShowForm(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name || '', code: d.code || '', description: d.description || '' });
    setFormError('');
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await axios.put(`${API}/departments/${editing.id}`,
          { ...form, is_active: editing.is_active !== false }, authHeaders());
      } else {
        await axios.post(`${API}/departments`, form, authHeaders());
      }
      setShowForm(false);
      showSuccess('Department saved');
      load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    const ok = await confirm(
      'Delete department',
      `Delete "${d.name}"? Existing records keep their stored department name; it just stops appearing in pickers.`,
      { destructive: true, confirmLabel: 'Delete' }
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/departments/${d.id}`, authHeaders());
      showSuccess('Department deleted');
      load();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to delete department');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#1B5C94]" />
          <h2 className="text-xl font-bold text-gray-900">Departments</h2>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-[#1B5C94] hover:bg-[#14496f]">
            <Plus className="w-4 h-4 mr-2" /> Add Department
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Tenant-wide list used across the Safety module (Users, Incidents, HIRA, CAPA, Documents, Audits, Training).
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : departments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No departments yet.{canManage ? ' Use “Add Department” to create one.' : ''}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Code</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                {canManage && <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {departments.map((d) => (
                <tr key={d.id} className={d.is_active === false ? 'opacity-60' : ''}>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">{d.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{d.code || '—'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{d.description || '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      d.is_active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                    }`}>
                      {d.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => openEdit(d)} className="text-gray-400 hover:text-blue-600 p-1" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(d)} className="text-gray-400 hover:text-red-600 p-1" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">{editing ? 'Edit' : 'Add'} Department</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Maintenance" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input type="text" value={form.code}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. MAINT" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} rows={2}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {formError && <div className="text-sm text-red-600">{formError}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1B5C94] hover:bg-[#14496f]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
